import { Link } from "react-router-dom";
import MaskedReveal from "@/components/MaskedReveal";
import FadeUp from "@/components/FadeUp";
import { ArrowUpRight } from "lucide-react";
import { PORTRAITS, PORTRAIT_ALT } from "@/data/portraits";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import { breadcrumbListJsonLd, medicalWebPageJsonLd, faqPageJsonLd } from "@/data/schema";

const CHAPTERS = [
  {
    n: "01", t: "One doctor. Every step.",
    b: "The consultant who reads your imaging is the consultant who does your procedure and reviews your follow-up. Vascular care is not modular — the person deciding must be the person doing.",
    detail: "You will not encounter a rotating panel of junior residents. You will not have a call centre schedule your consult. Dr Sidhu personally leads the intake video call, the procedure, and the six-week and six-month follow-ups.",
  },
  {
    n: "02", t: "Interventional, not open-first.",
    b: "Image-guided keyhole techniques, wherever they serve you better than open surgery. This is a specialist practice, not a general one.",
    detail: "Endovascular repair for aortic aneurysms. Embolisation for fibroids. Angioplasty for critical limb ischaemia. When a minimally invasive path is available and appropriate, it is offered. When it isn't, we say so and refer honestly to the right surgeon.",
  },
  {
    n: "03", t: "A second opinion, not a sales pitch.",
    b: "A stent is not always the answer. Sometimes the right recommendation is to wait, to monitor, or to change medication. You will leave with a plan you understand.",
    detail: "Many vascular findings do not require intervention. We tell you that. Where we do recommend a procedure, we walk you through why, the alternatives, the risks, and what a good outcome looks like — before you decide.",
  },
  {
    n: "04", t: "Continuity across borders.",
    b: "For NRI patients, pre-arrival video review, coordinated admission, and post-return follow-up — the same doctor from your home country to Mohali and back.",
    detail: "We work with patients from Canada, the UK, the US, Australia, the UAE, and the Gulf. Video consult and imaging review before you travel; admission coordinated with Max Mohali; post-departure follow-up on the same WhatsApp thread.",
  },
];

export default function WhyChoose() {
  return (
    <div data-testid="why-choose-page" className="pt-28 md:pt-36 pb-28">
      <SEO
        title="Why Dr Sidhu — Four reasons this vascular IR practice is different"
        description="One doctor at every step. Interventional-first, not open-first. Second opinions honestly given. Continuity across borders for NRI patients. Four commitments the practice is designed around."
        keywords={["why choose Dr Sidhu", "senior IR India", "one doctor continuity", "NRI vascular second opinion", "minimally invasive vs open surgery India"]}
        url="/why-choose"
      />
      <JsonLd data={[
        medicalWebPageJsonLd({ name: "Why choose Dr Tegbir Sidhu's practice", description: "Four commitments the practice is designed around — one doctor continuity, interventional-first care, honest second opinions, cross-border NRI follow-up.", url: "/why-choose" }),
        breadcrumbListJsonLd([{ label: "Home", to: "/" }, { label: "Why Dr Sidhu", to: "/why-choose" }]),
        faqPageJsonLd(CHAPTERS.map((c) => ({ q: c.t, a: c.b + " " + c.detail }))),
      ]}/>
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <FadeUp>
          <div className="kbd">— Why Dr Sidhu</div>
          <h1 className="mt-8 font-serif text-6xl md:text-[8.5vw] leading-[0.96] tracking-tightest max-w-5xl">
            <MaskedReveal text={"Four reasons"} />
            <MaskedReveal text={"this practice"} delay={0.1} />
            <MaskedReveal text={"is different."} delay={0.2} className="italic font-normal" />
          </h1>
        </FadeUp>

        <div className="mt-24 md:mt-32 space-y-24 md:space-y-32">
          {CHAPTERS.map((c, i) => (
            <FadeUp key={c.n} delay={0.05 * i}>
              <div className={`grid grid-cols-12 gap-6 md:gap-12 ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}>
                <div className="col-span-12 md:col-span-3 [direction:ltr]">
                  <div className="kbd">CHAPTER</div>
                  <div className="font-serif text-[9rem] md:text-[12rem] leading-none tracking-tightest text-crimson mt-2">{c.n}</div>
                </div>
                <div className="col-span-12 md:col-span-9 [direction:ltr]">
                  <h2 className="font-serif text-4xl md:text-6xl tracking-tightest leading-[1.04] max-w-3xl">
                    <MaskedReveal text={c.t} stagger={0.05}/>
                  </h2>
                  <p className="mt-6 text-xl md:text-2xl text-ink leading-relaxed max-w-3xl">{c.b}</p>
                  <p className="mt-6 text-base md:text-lg text-ink-muted leading-relaxed max-w-2xl">{c.detail}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-32">
          <div className="hairline-t pt-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <h3 className="font-serif text-4xl md:text-6xl tracking-tightest leading-tight max-w-2xl">
              Ready to talk to the doctor directly?
            </h3>
            <div className="flex gap-3">
              <Link to="/contact" data-testid="why-choose-cta" className="btn-pill-inv">Book consultation <ArrowUpRight size={14}/></Link>
              <Link to="/urgent" className="btn-crimson">Urgent evaluation</Link>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  );
}
